import React, { useContext, useEffect, useMemo, useState } from "react";
import DateFilterDropdown from "../../Shared/DateFilterDropdown/DateFilterDropdown";
import { InlineFilterSelect } from "../shared/FilterSelect";
import IndentSummaryTab from "../tabs/IndentSummaryTab";
import classes from "../MasterDashboard.module.css";
import { useIndentSummary } from "../hooks/useIndentSummary";
import { useTmsDashboardFilters } from "../hooks/useTmsDashboardFilters";
import useIsSuperAdmin from "../../../hooks/useIsSuperAdmin";
import { useMixPanelForTracking } from "../../../utils/TrackMixPanel";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../MyCompanyProfileContext";
import {
  ROLE_OPTIONS,
  PERIOD_PRESET_FILTERS,
  toIsoDate,
  formatRelativeTime,
  transformIndentSummary,
} from "../utils/transformers";

const IndentSummaryPanel = ({ viewOnly = false, onRestrictedClick = () => {}, isActive = true }) => {
  const isSuperAdmin = useIsSuperAdmin();
  const roleOptions = isSuperAdmin ? ROLE_OPTIONS : ROLE_OPTIONS.filter((opt) => opt.value !== "company_view");
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: "None",
      Definition: "user has visited indent summary tab",
    });
  }, []);

  const [role, setRole] = useState(ROLE_OPTIONS[0].value);
  useEffect(() => {
    if (!isSuperAdmin && role === "company_view") {
      setRole("user_view");
    }
  }, [isSuperAdmin, role]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return { startDate: toIsoDate(weekAgo), endDate: toIsoDate(today) };
  });
  const [plant, setPlant] = useState("All");
  const [destination, setDestination] = useState("All");
  const [transporterId, setTransporterId] = useState("All");
  const [dateFilterKey, setDateFilterKey] = useState(0);

  const { startDate, endDate } = dateRange;

  const handleDateChange = ({ fromDate, toDate, selectedFilter }) => {
    setDateRange({ startDate: toIsoDate(new Date(fromDate)), endDate: toIsoDate(new Date(toDate)) });
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: selectedFilter || `${fromDate} - ${toDate}`,
      Definition: "user has changed period filter",
    });
  };

  const handleRoleChange = (value) => {
    setRole(value);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: value,
      Definition: "user has changed role filter",
    });
  };

  const handlePlantChange = (value) => {
    setPlant(value);
    setDestination("All");
    setTransporterId("All");
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: value,
      Definition: "user has changed plant filter",
    });
  };

  const handleDestinationChange = (value) => {
    setDestination(value);
    setTransporterId("All");
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: value,
      Definition: "user has changed destination filter",
    });
  };

  const handleTransporterChange = (value) => {
    setTransporterId(value);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: value,
      Definition: "user has changed transporter filter",
    });
  };

  const handleResetFilters = () => {
    setRole(ROLE_OPTIONS[0].value);
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    setDateRange({ startDate: toIsoDate(weekAgo), endDate: toIsoDate(today) });
    setPlant("All");
    setDestination("All");
    setTransporterId("All");
    setDateFilterKey((k) => k + 1);
    TrackMixPanel({
      ModuleName: "master dashboard",
      ScreenName: "indent summary",
      Data: "None",
      Definition: "user has reset filters",
    });
  };

  const transporterIdParam = transporterId === "All" ? undefined : transporterId;
  const destinationParam = destination === "All" ? undefined : destination;
  const plantParam = plant === "All" ? undefined : plant;

  const { data: filtersData } = useTmsDashboardFilters({
    startDate,
    endDate,
    plant: plantParam,
    destination: destinationParam,
    transporterId: transporterIdParam,
    role,
    screen: "indent_summary",
  });

  const plantOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      ...(filtersData?.plants || [])
        .filter((p) => p.location_code)
        .map((p) => ({
          value: p.location_code,
          label: p.location_name,
          code: p.location_code,
        })),
    ],
    [filtersData]
  );

  const destinationOptions = useMemo(
    () => [
      { value: "All", label: "All" },
      ...(filtersData?.destinations || [])
        .filter((d) => d.location_code)
        .map((d) => ({
          value: d.location_code,
          label: d.location_name,
          code: d.location_code,
        })),
    ],
    [filtersData]
  );

  const transporterOptions = useMemo(() => {
    const transporters =
      filtersData?.transporters ||
      filtersData?.transporters_bid ||
      filtersData?.transporters_dispatched ||
      [];
    return [
      { value: "All", label: "All" },
      ...transporters.map((t) => ({ value: t.transporter_id, label: t.transporter_name })),
    ];
  }, [filtersData]);

  const { data: indentSummaryData, isLoading, dataUpdatedAt } = useIndentSummary(
    {
      startDate,
      endDate,
      plant,
      role,
      transporterId: transporterIdParam,
      destination: destinationParam,
    },
    isActive
  );

  const indentSummary = useMemo(
    () => (indentSummaryData ? transformIndentSummary(indentSummaryData) : null),
    [indentSummaryData]
  );

  const filteredIndentsByPlant = useMemo(() => {
    if (!indentSummary) return [];
    const withCode = indentSummary.byPlant.filter((row) => !row.isOthers && row.code);
    if (plant === "All") return withCode;
    return withCode.filter((row) => (row.code || row.name) === plant);
  }, [plant, indentSummary]);

  const allIndentsByPlant = useMemo(() => {
    if (!indentSummary) return [];
    const withoutOthers = indentSummary.byPlant.filter((row) => !row.isOthers);
    if (plant === "All") return withoutOthers;
    return withoutOthers.filter((row) => (row.code || row.name) === plant);
  }, [plant, indentSummary]);

  const filteredIndentsByTransporter = useMemo(() => {
    if (!indentSummary) return [];
    if (transporterId === "All") return indentSummary.byTransporter;
    return indentSummary.byTransporter.filter((row) => row.transporterId === transporterId);
  }, [transporterId, indentSummary]);

  const lastUpdated = formatRelativeTime(dataUpdatedAt, now);

  return (
    <>
      <div className={classes.controlsBar}>
        <InlineFilterSelect label="Role" value={role} onChange={handleRoleChange} options={roleOptions} />
        <div className={classes.inlineFilterGroup}>
          <span className={classes.inlineFilterLabel}>Period</span>
          <DateFilterDropdown
            key={dateFilterKey}
            onDateChange={handleDateChange}
            defaultFilter="1 Week"
            presetFilters={PERIOD_PRESET_FILTERS}
          />
        </div>
        <InlineFilterSelect label="Plant" value={plant} onChange={handlePlantChange} options={plantOptions} />
        <InlineFilterSelect
          label="Destination"
          value={destination}
          onChange={handleDestinationChange}
          options={destinationOptions}
        />
        <InlineFilterSelect
          label="Transporter"
          value={transporterId}
          onChange={handleTransporterChange}
          options={transporterOptions}
        />
        <button
          type="button"
          className={classes.resetFiltersButton}
          onClick={handleResetFilters}
          title="Reset all filters"
        >
          ↺ Reset
        </button>
        <div className={classes.liveStatus}>
          <span className={classes.liveDot} />
          Live · updated {lastUpdated}
        </div>
      </div>

      <div className={classes.pageBody}>
        {isLoading ? (
          <div className={classes.loadingCard}>
            <div className={classes.loadingSpinner} />
            <div className={classes.loadingText}>Loading indent summary…</div>
          </div>
        ) : indentSummary ? (
          <IndentSummaryTab
            indentSummary={indentSummary}
            filteredIndentsByPlant={filteredIndentsByPlant}
            allIndentsByPlant={allIndentsByPlant}
            filteredIndentsByTransporter={filteredIndentsByTransporter}
            viewOnly={viewOnly}
            onRestrictedClick={onRestrictedClick}
          />
        ) : null}
      </div>
    </>
  );
};

export default IndentSummaryPanel;
